import { PROJECT_DRAG_TYPE, SESSION_CHAT_ATTACH_TYPE } from '@/constants/chatDragTypes';
import type {
  ChatAttachedProjectPayload,
  ChatAttachedSessionPayload,
} from '@/components/Messenger/types/chatMessageTypes';
import { MESSENGER_MAX_CONTEXT_ATTACHMENTS_PER_MESSAGE } from './messengerAttachmentLimits';

export interface ChatAttachDropRead {
  projects: ChatAttachedProjectPayload[];
  sessions: ChatAttachedSessionPayload[];
}

/** Кладёт проект в dataTransfer для броска в чат — пара к readChatAttachFromDataTransfer (таблица и список нового дизайна). */
export function writeProjectChatAttach(dt: DataTransfer, project: { id: string; name?: string | null }): ChatAttachedProjectPayload {
  const payload: ChatAttachedProjectPayload = { id: project.id, name: project.name || project.id };
  dt.setData(PROJECT_DRAG_TYPE, JSON.stringify(payload));
  dt.setData('text/plain', payload.name);
  dt.effectAllowed = 'copy';
  return payload;
}

/** Читает проект/сессию из dataTransfer (DnD из таблицы проектов или бабла сессии). */
export function readChatAttachFromDataTransfer(dt: DataTransfer): ChatAttachDropRead {
  const projects: ChatAttachedProjectPayload[] = [];
  const sessions: ChatAttachedSessionPayload[] = [];

  if (dt.types.includes(PROJECT_DRAG_TYPE)) {
    try {
      const raw = dt.getData(PROJECT_DRAG_TYPE);
      const payload = JSON.parse(raw) as Partial<ChatAttachedProjectPayload>;
      if (payload?.id) {
        projects.push({ id: payload.id, name: payload.name || payload.id });
      }
    } catch {
      /* ignore */
    }
  }

  if (dt.types.includes(SESSION_CHAT_ATTACH_TYPE)) {
    try {
      const raw = dt.getData(SESSION_CHAT_ATTACH_TYPE);
      const payload = JSON.parse(raw) as Partial<ChatAttachedSessionPayload> & { id?: string; date?: string };
      if (payload?.id && payload?.date) {
        sessions.push({
          id: payload.id,
          date: payload.date,
          project_id: payload.project_id,
          label: payload.label || `${payload.date}`,
        });
      }
    } catch {
      /* ignore */
    }
  }

  return { projects, sessions };
}

/**
 * Не более одного контекстного вложения (проект или сессия) на сообщение.
 * Новый drop заменяет предыдущий контекст; при проекте и сессии в одном drop — берём проект.
 */
export function mergeChatContextAttachOneSlot(
  prevProjects: ChatAttachedProjectPayload[],
  prevSessions: ChatAttachedSessionPayload[],
  incoming: ChatAttachDropRead
): { projects: ChatAttachedProjectPayload[]; sessions: ChatAttachedSessionPayload[] } {
  const p0 = incoming.projects[0];
  const s0 = incoming.sessions[0];
  if (p0) {
    return { projects: [p0], sessions: [] };
  }
  if (s0) {
    return { projects: [], sessions: [s0] };
  }
  return { projects: prevProjects, sessions: prevSessions };
}

/** Формат `context_attachments` для POST direct-сообщения (совпадает с отображением в DirectMessageBubble). */
export function contextAttachmentsForDirectApi(
  projects: ChatAttachedProjectPayload[],
  sessions: ChatAttachedSessionPayload[]
): Array<{
  type: 'project' | 'session';
  id: string;
  name?: string;
  date?: string;
  label?: string;
  project_id?: string;
}> {
  const list = [
    ...projects.map((p) => ({ type: 'project' as const, id: p.id, name: p.name || p.id })),
    ...sessions.map((s) => ({
      type: 'session' as const,
      id: s.id,
      date: s.date,
      label: s.label || s.date,
      ...(s.project_id ? { project_id: s.project_id } : {}),
    })),
  ];
  return list.slice(0, MESSENGER_MAX_CONTEXT_ATTACHMENTS_PER_MESSAGE);
}
